// Blocks of site pages (decision 023, H5): a page is a list of blocks with parameters, like ITConf pages were made of shortcodes.
// The block registry lives on the server (render); the admin gets the same definitions without render to edit parameters.

/** `richtext` — a text written in the block editor (markdown, decision 034); `textarea` — lines of a format of its own (questions, conditions). */
export type BlockParamKind = 'text' | 'textarea' | 'richtext' | 'number' | 'checkbox' | 'select' | 'type' | 'field' | 'scope';

export interface BlockParam {
  key: string;
  label: string;
  kind: BlockParamKind;
  /** select: the choices; type/field/scope — the admin offers sections, fields of the chosen section, records of the scope type. */
  options?: { value: string; label: string }[];
  default?: string | number | boolean;
  help?: string;
  /** richtext: `line` — paragraphs with marks and links only (a subtitle, a note); `text` — also headings, lists and quotes. */
  blocks?: 'line' | 'text';
}

/** A kind of block as the registry declares it (core or an app manifest). */
export interface BlockDef { key: string; name: string; description: string; app: string | null; params: BlockParam[] }

/** One block on a page: which kind and its parameters. */
export interface PageBlock { id: string; key: string; params: Record<string, unknown> }

/** The blocks of a page from its stored field; anything malformed is dropped rather than breaking the page. */
export function readBlocks(v: unknown): PageBlock[] {
  let list: unknown = v;
  if (typeof v === 'string') { try { list = JSON.parse(v); } catch { return []; } }
  if (!Array.isArray(list)) return [];
  return list.filter((b): b is PageBlock => !!b && typeof b === 'object' && typeof (b as PageBlock).key === 'string' && typeof (b as PageBlock).id === 'string')
    .map((b) => ({ id: b.id, key: b.key, params: b.params && typeof b.params === 'object' ? b.params : {} }));
}

/** A page address: starts with «/», lowercase latin letters, digits, «-», «/»; `*` at the end — a template for the addresses under it. */
export const PAGE_PATH = /^\/([a-z0-9-]+(\/[a-z0-9-]+)*(\/\*)?)?$/;

/** Addresses the site serves itself: a page cannot take them. */
export const RESERVED_PATHS = ['/o', '/me', '/pay', '/f', '/legal', '/login', '/logout', '/robots.txt', '/sitemap.xml', '/sp', '/t', '/tv', '/hooks', '/preview', '/_fonts', '/cert-sample'];

/** What is wrong with a page address, in words, or null (J2: the builder and the server say the same). */
export function pagePathProblem(raw: string): string | null {
  const path = raw.trim();
  if (!path) return 'Укажите адрес, например /about';
  if (!PAGE_PATH.test(path)) return 'Адрес страницы — например, /about или /e/*: строчные латинские буквы, цифры и дефис';
  if (path === '/e' || (path.startsWith('/e/') && path !== '/e/*')) return 'Адрес /e/… занят страницами событий; шаблон для всех событий — /e/*';
  if (RESERVED_PATHS.some((r) => path === r || path.startsWith(`${r}/`))) return `Адрес ${path} занят служебной страницей сайта`;
  return null;
}
